import { realtimeUrl, type Message } from './client'

export type RealtimeEvent =
  | { type: 'message.created'; organization: string; conversation_id: string; message: Message }
  | { type: 'message.deleted'; organization: string; conversation_id: string; message_id: string }
  | { type: 'conversation.read'; organization: string; conversation_id: string; message_id: string | null }
  /** Sent locally after a reconnect: anything could have happened meanwhile, so refetch. */
  | { type: 'resync' }

type Listener = (event: RealtimeEvent) => void

const MAX_BACKOFF_MS = 30_000
const IDLE_CLOSE_MS = 5_000

/**
 * One socket per browser tab, shared by every component that listens. The server only
 * pushes news; everything that matters can be refetched over REST, so a dropped
 * connection is repaired by reconnecting and announcing a resync.
 */
class RealtimeConnection {
  private listeners = new Set<Listener>()
  private socket: WebSocket | null = null
  private attempts = 0
  private connectedOnce = false
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private idleTimer: ReturnType<typeof setTimeout> | null = null

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    if (this.idleTimer) {
      clearTimeout(this.idleTimer)
      this.idleTimer = null
    }
    this.connect()
    return () => {
      this.listeners.delete(listener)
      if (this.listeners.size === 0) {
        this.idleTimer = setTimeout(() => this.close(), IDLE_CLOSE_MS)
      }
    }
  }

  private connect() {
    if (this.socket || this.retryTimer || typeof WebSocket === 'undefined') return
    const url = realtimeUrl()
    if (!url) return

    const socket = new WebSocket(url)
    this.socket = socket
    socket.onmessage = (e) => {
      let event: any
      try {
        event = JSON.parse(e.data)
      } catch {
        return
      }
      if (event.type === 'hello') {
        this.attempts = 0
        if (this.connectedOnce) this.emit({ type: 'resync' })
        this.connectedOnce = true
        return
      }
      this.emit(event)
    }
    socket.onclose = (e) => {
      this.socket = null
      if (this.listeners.size === 0) return
      // 4401: the token was refused. Retrying with the same one would be refused
      // again, so wait for the slow retry, by which time a new login may have happened.
      const delay =
        e.code === 4401 ? MAX_BACKOFF_MS : Math.min(1000 * 2 ** this.attempts, MAX_BACKOFF_MS)
      this.attempts += 1
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null
        this.connect()
      }, delay)
    }
  }

  private close() {
    this.idleTimer = null
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
    const socket = this.socket
    this.socket = null
    this.connectedOnce = false
    socket?.close()
  }

  private emit(event: RealtimeEvent) {
    for (const listener of Array.from(this.listeners)) {
      try {
        listener(event)
      } catch (err) {
        console.error('Realtime listener failed', err)
      }
    }
  }
}

export const realtime = new RealtimeConnection()
